import { CANADIAN_PROVINCE_CODES, US_STATE_CODES } from '@/constants/geographies.js';
import { INVALID_EMAIL_USERNAMES, EMAIL_DOMAIN_TYPOS, KNOWN_GOOD_EMAIL_DOMAINS } from '@/constants/email-domains.js';
import { US_AREA_CODES, CANADIAN_AREA_CODES } from '@/constants/phone-area-codes.js';
import { extractDigits, normalizeWhitespace } from '@/utils/string-formatting.js';

// Allowlist model: a phone number is valid for a country only if its area code is a
// real geographic area code for that country (sourced from the NANPA feed via
// resources/data/area-codes/latest.json). Toll-free / non-geographic codes appear in
// neither list, so they are rejected automatically.
const VALID_CA_PHONE_PREFIXES = new Set(CANADIAN_AREA_CODES);
const VALID_US_PHONE_PREFIXES = new Set(US_AREA_CODES);
const INVALID_DATA_VALUES = new Set(['test', 'dsa', 'fdsa']);

const CA_POSTAL_REGEX = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ][ -]?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i;
const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;

const validators = {
    required: requiredValidation,
    name: nameValidation,
    email: emailValidation,
    emailConfirmation: emailConfirmationValidation,
    phone: phoneValidation,
    canadianPhone: canadianPhoneValidation,
    usPhone: usPhoneValidation,
    password: passwordValidation,
    passwordConfirmation: passwordConfirmationValidation,
    dob: dobValidation,
    paydate: paydateValidation,
    postal: postalValidation,
    zip: zipValidation,
    city: cityValidation,
    street: streetValidation,
    numeric: numericValidation,
    // what a rate field holds is already rounded (input-formats.js: 6.99 -> 7), so this checks for a
    // whole number like numeric (lms takes current_loan_rate as an integer), with a message about rates
    rate: numericValidation,
    sin: sinValidation,
    canadianProvince: canadianProvinceValidation,
    usState: usStateValidation,
    date: dateValidation,
    monthYear: monthYearValidation,
    inChoices: inChoicesValidation,
    driversLicense: driversLicenseValidation,
    driversLicenseUsFormat: driversLicenseUsFormatValidation,
    usGovermentIdNumber: usGovermentIdNumberValidation,
    usGovernmentIdNumber: usGovermentIdNumberValidation,
    bankRoutingNumber: bankRoutingNumberValidation,
    bankAccountNumber: bankAccountNumberValidation,
    bankName: bankNameValidation,
    propertyMortgage: propertyMortgageValidation,
    carYear: carYearValidation,
    ssn: ssnValidation,
    max: maxValidation,
};

const fieldValidationHelper = (fieldValidator, app = null, country = 'CA') => {
    let validator = {
        isValid: true,
        messages: [],
        invalidRules: []
    };

    const rules = fieldValidator?.rules;
    if (typeof rules === 'undefined' || !rules) {
        return validator;
    }

    const value = fieldValidator.value;

    // check for bogus data
    if (typeof value === 'string' && value) {
        const words = normalizeWhitespace(value).toLowerCase().split(' ');
        if (words.some(word => INVALID_DATA_VALUES.has(word))) {
            validator.isValid = false;
            validator.messages = ['__invalid_data__'];
            return validator;
        }
    }

    let allRules = rules.split('|').filter(rule => rule.length > 0);

    allRules.forEach((rule) => {
        let ruleName = rule;
        let ruleParam = null;

        if (rule.includes(':')) {
            [ruleName, ruleParam] = rule.split(':', 2);
        }

        const validatorFn = validators[ruleName];
        if (typeof validatorFn === 'function') {
            // The 4th argument (the whole validator entry) is only read by rules that need more
            // than the value, e.g. inChoices needs the field's options.
            if (!validatorFn(value, app, ruleParam, fieldValidator)) {
                validator.isValid = false;
                validator.invalidRules.push(ruleName);
            }
        } else {
            console.warn(`[field-validations] Unknown validation rule: "${rule}"`);
        }
    });

    validator.messages = getValidationMessages(validator.invalidRules, country);
    return validator;
}

const getValidationMessages = function getMessages(invalidRules = [], country = 'CA') {
    let messages = [];
    if (invalidRules.length > 0) {
        invalidRules.forEach((rule) => {
            switch (rule) {
                case 'email':
                    messages.push('__email_required__');
                    break;
                case 'emailConfirmation':
                    messages.push('__email_confirmation__');
                    break;
                case 'phone':
                    messages.push('__phone_format__');
                    break;
                case 'canadianPhone':
                    messages.push('__phone_canadian__');
                    break;
                case 'usPhone':
                    messages.push('__phone_us__');
                    break;
                case 'password':
                    messages.push('__password__');
                    break;
                case 'passwordConfirmation':
                    messages.push('__password_confirmation__');
                    break;
                case 'name':
                    messages.push('__name__');
                    break;
                case 'dob':
                    messages.push('__dob__');
                    break;
                case 'date':
                    messages.push('__date__');
                    break;
                case 'monthYear':
                    messages.push('__month_year__');
                    break;
                case 'inChoices':
                    messages.push('__in_choices__');
                    break;
                case 'paydate':
                    messages.push('__paydate__');
                    break;
                case 'postal':
                case 'postalCode':
                case 'zip':
                    if (country === 'US') {
                        messages.push('__zip_code_format__');
                    } else {
                        messages.push('__postal_code_format__');
                    }
                    break;
                case 'sin':
                    messages.push('__sin__');
                    break;
                case 'city':
                case 'street':
                    messages.push('__invalid_character__');
                    break;
                case 'numeric':
                    messages.push('__numeric__');
                    break;
                case 'rate':
                    messages.push('__rate__');
                    break;
                case 'carYear':
                    messages.push('__car_year__');
                    break;
                case 'driversLicense':
                    messages.push('__drivers_license__');
                    break;
                case 'driversLicenseUsFormat':
                    messages.push('Enter a valid driver\'s license number (numbers only)');
                    break;
                case 'usGovermentIdNumber':
                case 'usGovernmentIdNumber':
                    messages.push('Enter a valid ID number (alphanumeric only)');
                    break;
                case 'bankRoutingNumber':
                    messages.push('Enter a valid bank routing number (numbers only)');
                    break;
                case 'bankAccountNumber':
                    messages.push('Enter a valid bank account number (numbers only)');
                    break;
                case 'bankName':
                    messages.push('Enter a valid bank name');
                    break;
                case 'propertyMortgage':
                    messages.push('__property_mortgage__');
                    break;
                case 'ssn':
                    messages.push('Enter a valid SSN (9 digits, numbers only)');
                    break;
                case 'max':
                    messages.push('__max_length__');
                    break;
                default:
                    messages.unshift('__required_field__');
                    break;
            }
        });
    }
    return [...new Set(messages)];
}

const addressFieldValidationHelper = (addressValue, country = 'CA') => {

    let validator = {
        isValid: false,
        messages: ['__address_required__'],
        invalidFields: []
    };

    if (typeof addressValue === 'undefined' || !addressValue) {
        return validator;
    }

    const addressFieldsRules = {
        CA: {
            street: 'required',
            streetNumber: 'required',
            postalCode: 'required|postal',
            province: 'required|canadianProvince',
            city: 'required',
        },
        US: {
            street: 'required',
            streetNumber: 'required',
            postalCode: 'required|zip',
            province: 'required|usState',
            city: 'required',
        }
    };

    const rulesPerCountry = addressFieldsRules[country] ?? addressFieldsRules['CA'];

    // Iterate the rule set (not the address object's keys) so a field that is
    // absent from the address object is still checked — otherwise `required`
    // silently passes for missing keys.
    Object.keys(rulesPerCountry).forEach((field) => {
        let allRules = rulesPerCountry[field].split('|').filter(rule => rule.length > 0);
        const fieldValue = addressValue?.[field] ?? '';
        allRules.forEach((rule) => {
            const validatorFn = validators[rule];
            if (typeof validatorFn === 'function') {
                if (!validatorFn(fieldValue, null)) {
                    validator.invalidFields.push(field);
                }
            } else {
                console.warn(`[field-validations] Unknown validation rule: "${rule}"`);
            }
        });
    });

    validator.invalidFields = [...new Set(validator.invalidFields)];
    validator.isValid = validator.invalidFields.length === 0;
    validator.messages = validator.isValid ? [] : ['__address_required__'];
    return validator;
}

function paydateValidation(value) {
    if (!dateValidation(value)) {
        return false;
    }

    const [year, month, day] = value.split('-').map(Number);
    const input = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Must be strictly after today
    if (input <= today) {
        return false;
    }

    // Max 3 months from today — aligned with DatePicker's payday maxDate.
    // Clamp the day to avoid month-end overflow (e.g. Jan 31 + 3 months should be Apr 30, not May 1).
    let maxMonth = today.getMonth() + 3;
    let maxYear = today.getFullYear();
    if (maxMonth > 11) {
        maxMonth -= 12;
        maxYear += 1;
    }
    const maxDay = Math.min(today.getDate(), new Date(maxYear, maxMonth + 1, 0).getDate());
    const max = new Date(maxYear, maxMonth, maxDay);

    return input <= max;
}

function maxValidation(value, app, param) {
    const maxLen = parseInt(param, 10);
    if (isNaN(maxLen)) {
        return true;
    }
    if (typeof value !== 'string') {
        return true;
    }
    return value.length <= maxLen;
}

function requiredValidation(value) {
    if (typeof value === 'boolean') {
        return !!value;
    }

    if (typeof value !== 'string') {
        return !!value;
    }

    return !!value && normalizeWhitespace(value).length > 0 && value.length < 255;
}

function nameValidation(value) {
    if (!value) {
        return false;
    }

    let name = normalizeWhitespace(value);
    // Apostrophes for O'Neil and D'Amours: straight, or the curly one a prepop can carry (typing
    // turns it straight, see input-formats.js). lms accepts both.
    let regexCheck = (/^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s'\u2019-]*)$/.test(name));

    return name.length > 1 && name.length < 255 && regexCheck;
}

function phoneValidation(value) {
    // validating phone number:
    //  --  has to be 10 digits
    //  -- cant start with number 1
    if (!value || /^[2-9]\d{9}$/.test(value)) {
        return true;
    }
    return false;
}

function passwordValidation(value) {
    let regexCheck = (/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/.test(value));
    return regexCheck;
}

function passwordConfirmationValidation(value, appValues) {
    // requires passwordValidation()
    return passwordValidation(value) && appValues && appValues.password === value;
}

function emailConfirmationValidation(value, appValues) {
    // requires emailValidation()
    return emailValidation(value) && appValues && appValues.email === value;
}

/**
 * Opt-in rule for selects/radios whose value MUST be one of the options shown, e.g. the business
 * industry: an affiliate prepop can carry an old code (LAW_FIRM) that the select no longer offers,
 * which would show a blank select yet submit the old code. With this rule the applicant has to
 * pick from the list. Opt-in on purpose: other fields (amount radios) deliberately accept a
 * prefilled exact value that matches no bucket.
 *
 * Empty passes (`required` reports it). Fails open when the entry carries no options, e.g. the
 * prefill check that validates a bare { value, rules } pair.
 */
function inChoicesValidation(value, app, _param, fieldValidator) {
    if (value === null || value === undefined || value === '') {
        return true;
    }
    const choices = fieldValidator?.choices;
    const list = typeof choices === 'function' ? choices({ ...(app ?? {}) }) : choices;
    if (!Array.isArray(list)) {
        return true;
    }
    return list.some((choice) => String(choice.value) === String(value));
}

/**
 * Month-and-year pickers (business start date) store the 1st of the month as YYYY-MM-DD. An empty
 * value passes here on purpose: `required` reports it, so an empty field shows one message instead
 * of "required" plus a format message the applicant never typed.
 */
function monthYearValidation(value) {
    if (value === null || value === undefined || value === '') {
        return true;
    }
    return dateValidation(value);
}

function dateValidation(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
    }
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day;
}

function dobValidation(value) {
    if (!dateValidation(value)) {
        return false;
    }

    // Enforce age range (18–90 years) — mirrors DatePicker's dob min/max config.
    // The picker UI blocks out-of-range dates, but typed/prepop values bypass it.
    const [year, month, day] = value.split('-').map(Number);
    const today = new Date();
    const minAge = 18;
    const maxAge = 100;

    const minDob = new Date(today.getFullYear() - maxAge, 0, 1);
    const maxDob = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());

    const dob = new Date(year, month - 1, day);
    return dob >= minDob && dob <= maxDob;
}

function emailValidation(value) {
    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(value)) {
        const [username, domainPart] = value.toLowerCase().split('@');
        const tld = domainPart.split('.')[0];
        if (!INVALID_EMAIL_USERNAMES.has(username) && !EMAIL_DOMAIN_TYPOS.has(tld)) {
            return true;
        }
    }
    return false;
}

function sinValidation(value) {
    if (!value || /^\d{9}$/.test(value)) {
        return true;
    }
    return false;
}

function cityValidation(value) {
    let city = normalizeWhitespace(value);
    let regexCheck = (/^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s-&().']*)$/.test(city));

    return city.length > 1 && city.length < 255 && regexCheck;
}

function streetValidation(value) {
    let street = normalizeWhitespace(value);
    let regexCheck = (/^([0-9A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s-,#.&()']*)$/.test(street));
    return regexCheck;
}

function postalValidation(value) {
    return CA_POSTAL_REGEX.test(value);
}

function zipValidation(value) {
    return US_ZIP_REGEX.test(value);
}

function numericValidation(value) {
    let regexCheck = (/^[0-9]*$/.test(value));
    return regexCheck;
}

function countryPhoneValidation(value, validPrefixes, finalValidation = true) {
    if (typeof value !== 'string') {
        return false;
    }

    if (value && value.length >= 3) {
        const areaCode = value.substring(0, 3);
        // Reject numbers that can't be a NANP subscriber line (leading 1), and — unless
        // the allowlist failed to load (fail open) — any area code that isn't a real
        // geographic code for this country.
        if (value.startsWith('1') || (validPrefixes.size > 0 && !validPrefixes.has(areaCode))) {
            return false;
        }
    }

    // Only an explicit `false` (live typing in SmsVerification) skips the
    // final length check. `null`/`undefined` — e.g. the null ruleParam that
    // fieldValidationHelper passes for the `usPhone`/`canadianPhone` rules —
    // must still enforce 10 digits, otherwise short numbers slip through on
    // non-SMS-verified fields like the work phone.
    if (finalValidation !== false) {
        if (!/^\d{10}$/.test(value)) {
            return false;
        }
    }

    return true;
}

function canadianPhoneValidation(value, appValues, finalValidation = true) {
    return countryPhoneValidation(value, VALID_CA_PHONE_PREFIXES, finalValidation);
}

function usPhoneValidation(value, appValues, finalValidation = true) {
    return countryPhoneValidation(value, VALID_US_PHONE_PREFIXES, finalValidation);
}

function driversLicenseValidation(value) {
    return typeof value === 'string' && value.length > 0 && value === 'yes';
}

function driversLicenseUsFormatValidation(value) {
    return /^[A-Za-z0-9]{4,20}$/.test(value);
}

function usGovermentIdNumberValidation(value) {
    return /^[A-Za-z0-9]{4,20}$/.test(value);
}

function canadianProvinceValidation(value) {
    return typeof value === 'string' && value.length === 2 && CANADIAN_PROVINCE_CODES.includes(value);
}

function usStateValidation(value) {
    return typeof value === 'string' && value.length === 2 && US_STATE_CODES.includes(value);
}

function propertyMortgageValidation(value, appValues) {
    let propertyValue = Number(appValues?.property_value) || 0;
    let mortgageValue = Number(value) || 0;
    return propertyValue > 0 ? mortgageValue < propertyValue : mortgageValue <= propertyValue;
}

function carYearValidation(value) {
    if (!value || typeof value !== 'string' || value.length !== 4) {
        return false;
    }

    let allowedPastYears = 50;
    let allowedFutureYears = 1;
    let currentYear = new Date().getFullYear();
    let inputYear = Number(value) || 0;
    return inputYear >= (currentYear - allowedPastYears) && inputYear <= (currentYear + allowedFutureYears);
}

function ssnValidation(value) {
    if (!value || typeof value !== 'string' || value.length !== 9) {
        return false;
    }

    // Remove non-digit characters (if ever we allow ###-###-### format)
    const clean = extractDigits(value);

    // Must contain exactly 9 digits
    if (!/^\d{9}$/.test(clean)) {
        return false;
    }

    // Basic validation rules for SSN

    // {AAA}|{GG}|{SSSS}
    const area = clean.substring(0, 3);
    const group = clean.substring(3, 5);
    const serial = clean.substring(5, 9);

    // Area number cannot be 000, 666, or start with 9
    if (area === '000' || area === '666' || area.startsWith('9')) {
        return false;
    }

    // Group number cannot be 00
    if (group === '00') {
        return false;
    }

    // Serial number cannot be 0000
    if (serial === '0000') {
        return false;
    }

    // Commonly used invalid or test SSNs
    const invalidKnown = [
        '123456789',
        '078051120'
    ];
    if (invalidKnown.includes(clean)) {
        return false;
    }

    return true;
}

function bankRoutingNumberValidation(value) {
    if (!/^\d{9}$/.test(value)) {
        return false;
    }

    // US ABA routing number checksum:
    // multiply the 9 digits by repeating weights (3,7,1), sum the results, and
    // the routing number is valid if the total is divisible by 10 (sum % 10 === 0).
    const digits = value.split('').map(Number);
    const checksum =
        3 * (digits[0] + digits[3] + digits[6]) +
        7 * (digits[1] + digits[4] + digits[7]) +
        (digits[2] + digits[5] + digits[8]);

    return checksum % 10 === 0;
}

function bankAccountNumberValidation(value) {
    return /^\d{4,17}$/.test(value);
}

function bankNameValidation(value) {
    if (typeof value !== 'string') {
        return false;
    }
    const name = value.trim();
    if (name.length < 3 || name.length > 100) {
        return false;
    }
    return /^[A-Za-z][A-Za-z.\-&\s]*$/.test(name);
}

async function validateEmailWithBackend(email) {
    const domain = email.split('@')[1];
    if (KNOWN_GOOD_EMAIL_DOMAINS.has(domain)) {
        return { isValid: true };
    }

    try {
        const res = await axios.post('/portal/member/email-validation', { email });
        return { isValid: !!res.data.status };
    } catch {
        return { isValid: true };
    }
}

export {
    fieldValidationHelper,
    addressFieldValidationHelper,
    canadianPhoneValidation,
    usPhoneValidation,
    getValidationMessages,
    validateEmailWithBackend,
    postalValidation,
    zipValidation,
    CA_POSTAL_REGEX,
    US_ZIP_REGEX,
}
